<template>
    <div class="mp-page">
        <LoadingBar v-if="loading" message="Loading your profile..." />

        <div v-else class="mp-container">

            <!-- Header banner -->
            <div class="mp-header">
                <div class="mp-header-glow"></div>
                <div class="mp-avatar">
                    <img v-if="user.photo" :src="user.photo" alt="Avatar" />
                    <span v-else>{{ initials }}</span>
                </div>
                <div class="mp-header-info">
                    <h2>{{ user.first_name }} {{ user.last_name }}</h2>
                    <div class="mp-header-meta">
                        <span><i class="fa fa-hashtag"></i> {{ user.external_id || 'N/A' }}</span>
                        <span v-if="user.email"><i class="fa fa-envelope"></i> {{ user.email }}</span>
                        <span v-if="user.get_role"><i class="fa fa-user-shield"></i> {{ user.get_role.name }}</span>
                    </div>
                </div>
            </div>

            <!-- Two-column body -->
            <div class="mp-grid">

                <!-- Left: Profile info -->
                <div class="mp-card mp-card-info">
                    <div class="mp-card-head">
                        <div class="mp-card-icon"><i class="fa fa-id-card"></i></div>
                        <div>
                            <div class="mp-card-title">Profile Information</div>
                            <p class="mp-card-sub">Update your personal details</p>
                        </div>
                    </div>

                    <form id="profile_update_form" class="mp-form" @submit.prevent="saveInfo">
                        <!-- First Name -->
                        <div class="mb-4 mt-2">
                            <label class="form-label form-label-thin">First Name <span
                                    class="text-danger">*</span></label>
                            <input type="text" class="form-control form-control-thin" id="first_name" name="first_name"
                                ref="first_name" v-model="infoForm.first_name" placeholder="First Name" />
                            <span v-if="infoErrors.first_name" class="text-danger small">{{ infoErrors.first_name[0]
                                }}</span>
                        </div>

                        <!-- Last Name -->
                        <div class="mb-4">
                            <label class="form-label form-label-thin">Last Name <span
                                    class="text-danger">*</span></label>
                            <input type="text" class="form-control form-control-thin" id="last_name" name="last_name"
                                ref="last_name" v-model="infoForm.last_name" placeholder="Last Name" />
                            <span v-if="infoErrors.last_name" class="text-danger small">{{ infoErrors.last_name[0]
                                }}</span>
                        </div>

                        <!-- Email Address (Locked) -->
                        <div class="mb-4">
                            <label class="form-label form-label-thin">
                                Email Address
                                <span class="badge bg-secondary"><i class="fa fa-lock"></i> Locked</span>
                            </label>
                            <input type="email" class="form-control form-control-thin bg-light" id="email"
                                :value="infoForm.email" disabled readonly />
                            <span v-if="infoErrors.email" class="text-danger small">{{ infoErrors.email[0] }}</span>
                        </div>

                        <!-- Phone -->
                        <div class="mb-4">
                            <label class="form-label form-label-thin">Phone</label>
                            <input type="text" class="form-control form-control-thin" id="phone" name="phone"
                                ref="phone" v-model="infoForm.phone" placeholder="Phone Number" />
                            <span v-if="infoErrors.phone" class="text-danger small">{{ infoErrors.phone[0] }}</span>
                        </div>

                        <!-- Address -->
                        <div class="mb-4">
                            <label class="form-label form-label-thin">Address</label>
                            <textarea class="form-control form-control-thin" id="address" name="address" ref="address"
                                v-model="infoForm.address" placeholder="Full Address" rows="2"></textarea>
                            <span v-if="infoErrors.address" class="text-danger small">{{ infoErrors.address[0] }}</span>
                        </div>

                        <!-- Form Footer -->
                        <div class="mp-form-footer">
                            <transition name="mp-fade">
                                <span v-if="infoSuccess" class="mp-success">
                                    <i class="fa fa-check-circle"></i> Saved
                                </span>
                            </transition>
                            <button type="submit"
                                class="btn btn-primary py-1 px-2 text-light hover-white btn-sm form-submit-btn"
                                :disabled="savingInfo">
                                <i class="fa-solid fa-check fs-12" :class="{ 'fa-spin': savingInfo }"></i>
                                {{ savingInfo ? 'Saving…' : 'Save changes' }}
                            </button>
                        </div>
                    </form>
                </div>

                <!-- Right: Password -->
                <div class="mp-card mp-card-security">
                    <div class="mp-card-head">
                        <div class="mp-card-icon mp-card-icon-amber"><i class="fa fa-shield-halved"></i></div>
                        <div>
                            <div class="mp-card-title">Change Password</div>
                            <p class="mp-card-sub">
                                {{ pwStep === 'request'
                                    ? "We'll email a verification code before applying the change"
                                    : 'Enter the code sent to ' + (user.email || 'your email') }}
                            </p>
                        </div>
                    </div>

                    <!-- Step indicator -->
                    <div class="mp-steps">
                        <div class="mp-step" :class="{ active: pwStep === 'request', done: pwStep === 'confirm' }">
                            <span class="mp-step-dot"><i class="fa fa-check" v-if="pwStep === 'confirm'"></i><template
                                    v-else>1</template></span>
                            Details
                        </div>
                        <div class="mp-step-line" :class="{ filled: pwStep === 'confirm' }"></div>
                        <div class="mp-step" :class="{ active: pwStep === 'confirm' }">
                            <span class="mp-step-dot">2</span>
                            Verify
                        </div>
                    </div>

                    <!-- General/top-level error banner (e.g. server message outside field errors) -->
                    <transition name="mp-fade">
                        <div v-if="pwGeneralError" class="mp-alert">
                            <i class="fa fa-triangle-exclamation"></i>
                            <span>{{ pwGeneralError }}</span>
                        </div>
                    </transition>

                    <!--
                        Both steps stay permanently mounted and are toggled purely with v-show.
                        v-if/v-else + <transition mode="out-in"> destroys/recreates the <form>
                        nodes on every step change, and that raced with the nested success
                        message transition + Vue's patch cycle, producing "insertBefore" /
                        "not a child of this node" crashes. v-show only flips CSS display,
                        so nothing is ever created or destroyed and there's nothing to race.
                    -->
                    <div class="mp-step-wrapper">
                        <!-- Step 1: current + new password -->
                        <form v-show="pwStep === 'request'" id="password_request_form" class="mp-form"
                            @submit.prevent="requestCode">
                            <div class="mp-field">
                                <label>Current password</label>
                                <div class="mp-input-wrap" :class="{ 'has-error': pwErrors.current_password }">
                                    <i class="fa fa-lock"></i>
                                    <input v-model="pwRequestForm.current_password" name="current_password"
                                        :type="showCurrentPassword ? 'text' : 'password'" placeholder="••••••••"
                                        autocomplete="current-password" />
                                    <button type="button" class="mp-eye-btn"
                                        :aria-label="showCurrentPassword ? 'Hide password' : 'Show password'"
                                        @click="showCurrentPassword = !showCurrentPassword">
                                        <i class="fa" :class="showCurrentPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                                    </button>
                                </div>
                                <span v-if="pwErrors.current_password" class="mp-error">
                                    <i class="fa fa-circle-exclamation"></i> {{ pwErrors.current_password[0] }}
                                </span>
                            </div>

                            <div class="mp-field">
                                <label>New password</label>
                                <div class="mp-input-wrap" :class="{ 'has-error': pwErrors.new_password }">
                                    <i class="fa fa-key"></i>
                                    <input v-model="pwRequestForm.new_password" name="new_password"
                                        :type="showNewPassword ? 'text' : 'password'" placeholder="••••••••"
                                        autocomplete="new-password" />
                                    <button type="button" class="mp-eye-btn"
                                        :aria-label="showNewPassword ? 'Hide password' : 'Show password'"
                                        @click="showNewPassword = !showNewPassword">
                                        <i class="fa" :class="showNewPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                                    </button>
                                </div>
                                <span v-if="pwErrors.new_password" class="mp-error">
                                    <i class="fa fa-circle-exclamation"></i> {{ pwErrors.new_password[0] }}
                                </span>
                            </div>

                            <div class="mp-field">
                                <label>Confirm new password</label>
                                <div class="mp-input-wrap" :class="{ 'has-error': pwErrors.confirm_password }">
                                    <i class="fa fa-key"></i>
                                    <input v-model="pwRequestForm.confirm_password" name="confirm_password"
                                        :type="showConfirmPassword ? 'text' : 'password'" placeholder="••••••••"
                                        autocomplete="new-password" />
                                    <button type="button" class="mp-eye-btn"
                                        :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
                                        @click="showConfirmPassword = !showConfirmPassword">
                                        <i class="fa" :class="showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                                    </button>
                                </div>
                                <span v-if="pwErrors.confirm_password" class="mp-error">
                                    <i class="fa fa-circle-exclamation"></i> {{ pwErrors.confirm_password[0] }}
                                </span>
                            </div>

                            <div class="mp-form-footer">
                                <span v-if="pwSuccess" class="mp-success"><i class="fa fa-check-circle"></i>
                                    Password updated</span>
                                <button type="submit" class="mp-btn mp-btn-amber" :disabled="sendingCode">
                                    <i class="fa fa-spinner fa-spin" v-if="sendingCode"></i>
                                    <i class="fa fa-paper-plane" v-else></i>
                                    {{ sendingCode ? 'Sending…' : 'Send verification code' }}
                                </button>
                            </div>
                        </form>

                        <!-- Step 2: confirm code -->
                        <form v-show="pwStep === 'confirm'" id="password_confirm_form" class="mp-form"
                            @submit.prevent="confirmCode">
                            <div class="mp-field mp-code-field">
                                <label>Verification code</label>
                                <div class="mp-input-wrap mp-input-wrap-code" :class="{ 'has-error': pwErrors.code }">
                                    <i class="fa fa-shield-halved"></i>
                                    <input v-model="pwConfirmForm.code" type="text" inputmode="numeric" maxlength="6"
                                        placeholder="• • • • • •" />
                                </div>
                                <span v-if="pwErrors.code" class="mp-error">
                                    <i class="fa fa-circle-exclamation"></i> {{ pwErrors.code[0] }}
                                </span>
                            </div>

                            <div class="mp-form-footer">
                                <button type="button" class="mp-btn mp-btn-ghost" @click="backToRequest">
                                    <i class="fa fa-arrow-left"></i> Back
                                </button>
                                <button type="submit" class="mp-btn mp-btn-amber" :disabled="confirmingCode">
                                    <i class="fa fa-spinner fa-spin" v-if="confirmingCode"></i>
                                    <i class="fa fa-key" v-else></i>
                                    {{ confirmingCode ? 'Confirming…' : 'Confirm password' }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'

export default {
    name: 'MyProfile',
    components: { LoadingBar },

    data() {
        return {
            loading: false,
            user: {},

            infoForm: {
                id: '',
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                address: ''
            },
            infoErrors: {},
            savingInfo: false,
            infoSuccess: false,

            pwStep: 'request', // 'request' -> 'confirm'
            pwRequestForm: {
                current_password: '',
                new_password: '',
                confirm_password: ''
            },
            pwConfirmForm: {
                code: ''
            },
            pwErrors: {},
            pwGeneralError: '',
            sendingCode: false,
            confirmingCode: false,
            pwSuccess: false,

            showCurrentPassword: false,
            showNewPassword: false,
            showConfirmPassword: false
        }
    },

    computed: {
        initials() {
            const f = this.user.first_name?.[0] || ''
            const l = this.user.last_name?.[0] || ''
            return (f + l).toUpperCase()
        }
    },

    mounted() {
        this.fetchProfile()
    },

    methods: {
        async fetchProfile() {
            this.loading = true
            const result = await form_api_submit({
                url: 'profile',
                method: 'GET',
                data: {},
                show_success_toast: false,
                show_error_toast: true,
                remove_pre_toasts: false
            })
            if (result.success) {
                this.user = result.data.user
                this.infoForm.id = this.user.external_id || ''
                this.infoForm.first_name = this.user.first_name || ''
                this.infoForm.last_name = this.user.last_name || ''
                this.infoForm.email = this.user.email || ''
                this.infoForm.phone = this.user.phone || ''
                this.infoForm.address = this.user.address || ''
            }
            this.loading = false
        },

        async saveInfo() {
            this.savingInfo = true
            this.infoErrors = {}
            this.infoSuccess = false

            try {
                const payload = {
                    first_name: this.infoForm.first_name,
                    last_name: this.infoForm.last_name,
                    phone: this.infoForm.phone,
                    address: this.infoForm.address || null,
                }

                const result = await form_api_submit({
                    url: 'profile/update',
                    method: 'POST',
                    data: payload,
                    curr_form_id: '#profile_update_form',
                    show_success_toast: true,
                    show_error_toast: true,
                    remove_pre_toasts: false,
                })

                if (result.success) {
                    this.user = {
                        ...this.user,
                        ...result.data.user,
                    }

                    this.infoSuccess = true
                    setTimeout(() => {
                        this.infoSuccess = false
                    }, 2500)
                } else {
                    this.infoErrors = result.data?.errors || {}
                }
            } finally {
                this.savingInfo = false
            }
        },
        applyPasswordResponse(result) {
            this.pwErrors = {}
            this.pwGeneralError = ''

            if (result.success) {
                return true
            }

            const data = result.data || {}
            if (data.error_type === 'validation' && data.errors) {
                this.pwErrors = data.errors
            } else {
                this.pwGeneralError = result.message || 'Something went wrong. Please try again.'
            }
            return false
        },

        backToRequest() {
            this.pwStep = 'request'
            this.pwErrors = {}
            this.pwGeneralError = ''
        },

        async requestCode() {
            this.sendingCode = true

            const result = await form_api_submit({
                url: 'profile/password/request',
                method: 'POST',
                data: this.pwRequestForm,
                curr_form_id: '#password_request_form',
                show_success_toast: true,
                show_error_toast: true,
                remove_pre_toasts: false
            })

            if (this.applyPasswordResponse(result)) {
                this.pwConfirmForm.code = ''
                this.pwStep = 'confirm'
            }

            this.sendingCode = false
        },

        async confirmCode() {
            this.confirmingCode = true

            const result = await form_api_submit({
                url: 'profile/password/confirm',
                method: 'POST',
                data: this.pwConfirmForm,
                curr_form_id: '#password_confirm_form',
                show_success_toast: true,
                show_error_toast: true,
                remove_pre_toasts: false
            })

            if (this.applyPasswordResponse(result)) {
                this.pwRequestForm.current_password = ''
                this.pwRequestForm.new_password = ''
                this.pwRequestForm.confirm_password = ''
                this.showCurrentPassword = false
                this.showNewPassword = false
                this.showConfirmPassword = false
                this.pwStep = 'request'
                this.pwSuccess = true
                setTimeout(() => (this.pwSuccess = false), 2500)
            }

            this.confirmingCode = false
        }
    }
}
</script>

<style scoped>
* {
    box-sizing: border-box;
}

.mp-page {
    padding: 32px 24px;
    background:
        radial-gradient(circle at 10% -10%, rgba(99, 102, 241, 0.08), transparent 40%),
        radial-gradient(circle at 90% 0%, rgba(236, 72, 153, 0.06), transparent 35%),
        #f6f7fb;
    min-height: 100%;
}

.mp-container {
    max-width: 1100px;
    margin: 0 auto;
}

/* ── Header banner ── */
.mp-header {
    position: relative;
    display: flex;
    align-items: center;
    gap: 20px;
    background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 55%, #a855f7 100%);
    border-radius: 20px;
    padding: 28px 30px;
    margin-bottom: 26px;
    overflow: hidden;
    box-shadow: 0 12px 30px -10px rgba(79, 70, 229, 0.45);
}

.mp-header-glow {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(circle at 85% 20%, rgba(255, 255, 255, 0.18), transparent 45%),
        radial-gradient(circle at 15% 90%, rgba(255, 255, 255, 0.1), transparent 40%);
    pointer-events: none;
}

.mp-avatar {
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 18px;
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.16);
    border: 1.5px solid rgba(255, 255, 255, 0.35);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 24px;
    font-weight: 700;
    overflow: hidden;
}

.mp-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.mp-header-info {
    position: relative;
    flex: 1;
    min-width: 0;
}

.mp-header-info h2 {
    font-size: 20px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 8px;
    letter-spacing: -0.2px;
}

.mp-header-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.85);
}

.mp-header-meta span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.mp-header-meta i {
    color: rgba(255, 255, 255, 0.65);
    font-size: 11px;
}

.mp-status {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    padding: 7px 14px;
    border-radius: 20px;
    white-space: nowrap;
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.mp-status .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #fff;
}

.mp-status.is-active .dot {
    background: #4ade80;
}

.mp-status.is-inactive .dot {
    background: #3853ae;
}

/* ── Grid ── */
.mp-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 22px;
    align-items: start;
}

@media (max-width: 860px) {
    .mp-grid {
        grid-template-columns: 1fr;
    }
}

/* ── Cards ── */
.mp-card {
    background: #fff;
    border: 1px solid #eef0f3;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
    border-top: 3px solid transparent;
    transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.mp-card:hover {
    box-shadow: 0 10px 28px -12px rgba(16, 24, 40, 0.14);
}

.mp-card-info {
    border-top-color: #6366f1;
}

.mp-card-security {
    border-top-color: #3853ae;
}

.mp-card-head {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #f3f4f6;
}

.mp-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.14), rgba(139, 92, 246, 0.14));
    color: #6366f1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
}

.mp-card-icon-amber {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.14), rgba(249, 115, 22, 0.14));
    color: #3853ae;
}

.mp-card-title {
    font-size: 15px;
    font-weight: 700;
    color: #1f2937;
}

.mp-card-sub {
    font-size: 12px;
    color: #9ca3af;
    margin: 4px 0 0;
    line-height: 1.5;
}

/* ── Step indicator (password card) ── */
.mp-steps {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 18px 24px 0;
}

.mp-step {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 11.5px;
    font-weight: 600;
    color: #b0b4bd;
    white-space: nowrap;
}

.mp-step.active,
.mp-step.done {
    color: #3853ae;
}

.mp-step-dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #f3f4f6;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
    transition: background 0.2s ease, color 0.2s ease;
}

.mp-step.active .mp-step-dot,
.mp-step.done .mp-step-dot {
    background: linear-gradient(135deg, #3853ae, #3853ae);
    color: #fff;
}

.mp-step-line {
    flex: 1;
    height: 2px;
    background: #f3f4f6;
    border-radius: 2px;
    transition: background 0.2s ease;
}

.mp-step-line.filled {
    background: linear-gradient(90deg, #3853ae, #3853ae);
}

/* ── General error banner ── */
.mp-alert {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 16px 24px 0;
    padding: 11px 14px;
    border-radius: 12px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 12.5px;
    line-height: 1.5;
}

.mp-alert i {
    margin-top: 1px;
    font-size: 13px;
    flex-shrink: 0;
}

/* ── Shared form layout (Profile Info form) ── */
.mp-step-wrapper {
    position: relative;
}

.mp-form {
    padding: 20px 24px 24px;
}

.form-label-thin {
    font-size: 12.5px;
    font-weight: 600;
    color: #4b5563;
    margin-bottom: 7px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.form-control-thin {
    border: 1px solid #e5e7eb;
    border-radius: 11px;
    background: #fff;
    padding: 11px 14px;
    font-size: 13.5px;
    color: #1f2937;
    width: 100%;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.form-control-thin::placeholder {
    color: #b0b4bd;
}

.form-control-thin:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

.form-control-thin:disabled,
.form-control-thin[readonly] {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
}

textarea.form-control-thin {
    resize: vertical;
    min-height: 64px;
}

.mb-4 small.text-muted {
    display: block;
    font-size: 11.5px;
    color: #b0b4bd;
    margin-top: 5px;
}

.mb-4 .text-danger.small {
    display: block;
    font-size: 11.5px;
    color: #ef4444;
    margin-top: 5px;
}

/* ── Password card fields (icon style) ── */
.mp-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
}

.mp-field:first-child {
    margin-top: 4px;
}

.mp-field label {
    font-size: 12.5px;
    font-weight: 600;
    color: #4b5563;
    margin-bottom: 7px;
}

.mp-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
    border: 1px solid #e5e7eb;
    border-radius: 3px;
    background: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.mp-input-wrap > i:first-child {
    padding-left: 13px;
    color: #b0b4bd;
    font-size: 12.5px;
    width: 30px;
    text-align: center;
    flex-shrink: 0;
    transition: color 0.15s ease;
}

.mp-input-wrap input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    padding: 11px 8px 11px 4px;
    font-size: 13.5px;
    color: #1f2937;
    font-family: inherit;
}

.mp-input-wrap:focus-within {
    border-color: #3853ae;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.12);
}

.mp-input-wrap:focus-within > i:first-child {
    color: #3853ae;
}

.mp-input-wrap.has-error {
    border-color: #fca5a5;
    background: #fef7f7;
}

.mp-input-wrap.has-error:focus-within {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}

.mp-eye-btn {
    border: none;
    background: transparent;
    color: #b0b4bd;
    width: 34px;
    height: 34px;
    margin-right: 3px;
    border-radius: 8px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12.5px;
    cursor: pointer;
    transition: color 0.15s ease, background 0.15s ease;
}

.mp-eye-btn:hover {
    color: #3853ae;
    background: rgba(245, 158, 11, 0.1);
}

.mp-eye-btn:focus-visible {
    outline: 2px solid #3853ae;
    outline-offset: 1px;
}

.mp-input-wrap-code {
    justify-content: center;
}

.mp-input-wrap-code input {
    letter-spacing: 6px;
    font-weight: 700;
    font-size: 16px;
    text-align: center;
    padding-right: 13px;
}

.mp-error {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    font-size: 11.5px;
    color: #ef4444;
    margin-top: 6px;
    line-height: 1.4;
}

.mp-error i {
    font-size: 10.5px;
    margin-top: 2px;
    flex-shrink: 0;
}

.mp-form-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 14px;
    margin-top: 4px;
    padding-top: 16px;
    border-top: 1px solid #f3f4f6;
}

.mp-success {
    font-size: 12.5px;
    color: #16a34a;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-right: auto;
}

/* ── Buttons ── */
.form-submit-btn {
    border: none;
    border-radius: 11px;
    padding: 10px 19px !important;
    font-size: 13px !important;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: linear-gradient(135deg, #6366f1, #7c3aed) !important;
    box-shadow: 0 6px 16px -6px rgba(99, 102, 241, 0.55);
    transition: box-shadow 0.15s ease, opacity 0.15s ease, transform 0.1s ease;
}

.form-submit-btn:hover:not(:disabled) {
    box-shadow: 0 8px 20px -6px rgba(99, 102, 241, 0.65);
}

.form-submit-btn:active:not(:disabled) {
    transform: translateY(1px);
}

.form-submit-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
}

.mp-btn {
    border: none;
    border-radius: 11px;
    padding: 10px 19px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    transition: background 0.15s ease, opacity 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
}

.mp-btn:active:not(:disabled) {
    transform: translateY(1px);
}

.mp-btn-amber {
    background: linear-gradient(135deg, #3853ae, #529aa0);
    color: #fff;
    box-shadow: 0 6px 16px -6px rgb(90 156 161);
}

.mp-btn-amber:hover:not(:disabled) {
    box-shadow: 0 8px 20px -6px rgb(90 156 161);
}

.mp-btn-amber:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
}

.mp-btn-ghost {
    background: none;
    color: #6b7280;
    padding: 10px 6px;
    margin-right: auto;
}

.mp-btn-ghost:hover {
    color: #1f2937;
}

/* ── Transitions ── */
.mp-fade-enter-active,
.mp-fade-leave-active {
    transition: opacity 0.2s ease;
}

.mp-fade-enter-from,
.mp-fade-leave-to {
    opacity: 0;
}
</style>